import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import classNames from 'classnames';
import { hasValue } from "@utils";
import { FontAwesomeIconWrapper } from "./fontawesome/FontAwesomeIconWrapper";
import { PHIconWrapper } from "./phosphor/PHIconWrapper";
import { SkeletonLoader } from "@widget/loaders";
import { parseIcon, getSizeValue, getAnimationClasses, getTransformStyle } from "./utils";
/**
 * @export
 * Universal Icon component that handles FontAwesome, Phosphor, URLs, and text/emoji icons
 * Supports size and animations for all icon types
 *
 * @example FontAwesome with IconProp
 * ```
 * <Icon icon={['fas', 'save']} size="2x" spin />
 * ```
 *
 * @example FontAwesome with string
 * ```
 * <Icon icon="fas save" />
 * <Icon icon="far trash" color="red" size="2x" spin />
 * ```
 *
 * @example Phosphor with PHIconProp
 * ```
 * <Icon icon={['phb', 'house']} size="lg" />
 * ```
 *
 * @example Phosphor with string
 * ```
 * <Icon icon="phb house" />
 * <Icon icon="phf heart" color="red" size="lg" beat />
 * ```
 *
 * @example Images with size and animations
 * ```
 * <Icon icon="https://example.com/logo.svg" size="2x" spin />
 * <Icon icon="data:image/svg+xml;base64,..." width={50} height={50} />
 * ```
 *
 * @example Text and emoji with size and animations
 * ```
 * <Icon icon="×" fontSize="24px" />
 * <Icon icon="💾" size="2x" bounce />
 * ```
 */
const Icon = (props) => {
    const { icon, className, style, isLoading = false, skeletonLoader } = props;
    if (!hasValue(icon))
        return null;
    // Parse icon to determine type and format
    const parsed = parseIcon(icon);
    if (!parsed)
        return null;
    function renderContent() {
        // Common props
        const commonAnimationProps = {
            spin: props.spin,
            pulse: props.pulse,
            beat: props.beat,
            bounce: props.bounce,
            shake: props.shake,
            fade: props.fade,
        };
        const commonTransformProps = {
            flip: props.flip,
            rotation: props.rotation,
        };
        switch (parsed.type) {
            case 'react':
                return parsed.formatted;
            case 'fontawesome': {
                const p = props;
                return (_jsx(FontAwesomeIconWrapper, Object.assign({ icon: parsed.formatted, color: p.color, size: p.size, skeletonLoader: p.skeletonLoader }, commonTransformProps, commonAnimationProps)));
            }
            case 'phosphor': {
                const p = props;
                return (_jsx(PHIconWrapper, Object.assign({ icon: parsed.formatted, color: p.color, size: p.size, skeletonLoader: p.skeletonLoader }, commonTransformProps, commonAnimationProps)));
            }
            case 'image': {
                const p = props;
                const animationClasses = getAnimationClasses(commonAnimationProps);
                const transformStyle = getTransformStyle(p.flip, p.rotation);
                const sizeValue = getSizeValue(p.size, false);
                const imageStyle = Object.assign({ backgroundImage: `url(${parsed.formatted})`, width: p.width || sizeValue, height: p.height || sizeValue }, (transformStyle && { transform: transformStyle }));
                return (_jsx("div", { className: classNames('uxpcore_icon__img', ...animationClasses), style: imageStyle }));
            }
            case 'text': {
                const p = props;
                const animationClasses = getAnimationClasses(commonAnimationProps);
                const transformStyle = getTransformStyle(p.flip, p.rotation);
                const sizeValue = getSizeValue(p.size);
                const textStyle = Object.assign({ fontSize: p.fontSize || sizeValue, color: p.color }, (transformStyle && { transform: transformStyle }));
                return (_jsx("div", { className: classNames('uxpcore_icon__text', ...animationClasses), style: textStyle, children: parsed.formatted }));
            }
            default:
                return null;
        }
    }
    // Default skeleton loader configuration
    const defaultSkeletonProps = {
        width: "100%",
        height: "100%",
        additionalStyle: {
            borderRadius: "50%",
            aspectRatio: "1",
        }
    };
    const mergedSkeletonProps = skeletonLoader
        ? Object.assign(Object.assign(Object.assign({}, defaultSkeletonProps), skeletonLoader), { additionalStyle: Object.assign(Object.assign({}, defaultSkeletonProps.additionalStyle), skeletonLoader.additionalStyle) }) : defaultSkeletonProps;
    // Add type-specific class for styling
    const typeClass = `uxpcore_icon--${parsed.type}`;
    return (_jsx("div", { className: classNames('uxpcore_icon', typeClass, className), style: style, children: isLoading ? (_jsx(SkeletonLoader, Object.assign({}, mergedSkeletonProps))) : (_jsx(_Fragment, { children: renderContent() })) }));
};
export default Icon;
